import { FC, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PageContextWrapper } from "@ryde/controls";
import { useBookingsActions, useRideStationActions } from "@ryde/redux";
import {
  BOOKING_UPLOAD_FORM_FIELDS,
  bookingBulkCreateStateFromPollingState,
  bulkApprovalBookingConflictsUpdatedAtom,
  errorAndToastSimpleResult,
  errors,
  getDirectionFromPassengersData,
  IBookingPrepareResult,
  IBulkSetBookingsDto,
  IBulkUploadBookingsForm,
  IBulkUploadBookingsProps,
  IInjectedDrawerProps,
  isBulkBookingSetFinishSelector,
  Log,
  resolveBulkUploadBookingsCount,
  unique,
  useDrawers,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer } from "../../drawer";
import {
  BulkCreateBookingFooter,
  bulkUploadBookingsCsvFile,
  getApprovalBookingConflictAPI,
  IUseBulkCreateGetPayloadParams,
  PreviewStepForm,
  useBulkCreateBooking,
  useBulkUploadStationCreateFields,
} from "../bulk-bookings-components";
import { BulkUploadBookingsForm } from "./bulk-upload-bookings-form";

export const BulkUploadBookingsSuspended: FC<
  IBulkUploadBookingsProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const { openBookingProduction } = useDrawers();
  const [prepareResult, setPrepareResult] =
    useState<IBookingPrepareResult | null>(null);
  const { bookingsPrepare } = useBookingsActions();
  const { fetchStations } = useRideStationActions();
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);
  const bulkApprovalBookingConflicts = useRecoilValue(
    bulkApprovalBookingConflictsUpdatedAtom,
  );
  const {
    newBookingsIds,
    isLoading: isBulkBookingSetProceeding,
    totalCount: pollingTotalCount,
  } = useRecoilValue(bookingBulkCreateStateFromPollingState);
  const initialEntityCreateFields = useBulkUploadStationCreateFields();

  const methods = useForm<IBulkUploadBookingsForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      conflictsDetailsCreateIfExisting: "false",
      conflictsDetailsCreateIfInside: "false",
      conflictsDetailsCreateIfOverlap: "false",
      conflictsDetailsCreateIfGap: false,
      conflictsDetailsCreateIfIneligible: "false",
      entityCreateFields: initialEntityCreateFields,
    },
  });
  const {
    getValues,
    trigger,
    formState: { isDirty },
  } = methods;

  // After a bulk upload that may have auto-created stations, refresh the station list
  // (dropdownStationListAtom) so new stations resolve to a name in the bookings grid without a page reload.
  useEffect(() => {
    if (!isBulkBookingSetFinish) {
      return;
    }

    const { entityCreateFields } = getValues();

    if (entityCreateFields?.some(field => field.isAllow)) {
      void fetchStations(true);
    }
  }, [isBulkBookingSetFinish, fetchStations, getValues]);

  const passengersData = useMemo(() => {
    return prepareResult?.data || [];
  }, [prepareResult?.data]);
  // RYDE-8171: a meal-only shift row books once even when AB, which only the server knows - trust its total once reported.
  const bookingCount = resolveBulkUploadBookingsCount({
    passengersData,
    pollingTotalCount,
  });

  const bulkApprovalBookingConflictsApproved = Array.from(
    bulkApprovalBookingConflicts.values(),
  )
    .flat()
    .filter(({ approvalReason }) => !approvalReason);

  const successfullyBooked =
    bookingCount - bulkApprovalBookingConflictsApproved.length;

  const getPayload = useCallback(
    ({
      isPreview,
      approvalConflicts,
    }: IUseBulkCreateGetPayloadParams): IBulkSetBookingsDto => {
      const {
        conflictsDetailsCreateIfExisting,
        conflictsDetailsCreateIfInside,
        conflictsDetailsCreateIfOverlap,
        conflictsDetailsCreateIfGap,
        conflictsDetailsCreateIfIneligible,
      } = getValues();

      const payload: IBulkSetBookingsDto = {
        passengersData,
        isPreview,
        conflicts: {
          createIfExisting: conflictsDetailsCreateIfExisting === "true",
          createIfInside: conflictsDetailsCreateIfInside === "true",
          createIfOverlap: conflictsDetailsCreateIfOverlap === "true",
          createIfGap: conflictsDetailsCreateIfGap,
          createIfIneligible: conflictsDetailsCreateIfIneligible === "true",
        },
        approvalBookingConflicts:
          getApprovalBookingConflictAPI(approvalConflicts),
      };

      return payload;
    },
    [getValues, passengersData],
  );

  const {
    onCloseClick,
    saveLabel,
    onSaveClick,
    cancelLabel,
    onCancelClick,
    isPreviewStep,
    refreshHandler,
    abortHandler,
    setBulkBookingStateToForm,
    fetchBookings,
  } = useBulkCreateBooking({
    onCloseDrawer,
    getPayload,
    isDirty,
    trigger,
  });

  const isProduceButtonActive = Boolean(
    isPreviewStep && newBookingsIds?.length && isBulkBookingSetFinish,
  );

  const onProduce = useEvent(async (): Promise<void> => {
    const handleProduceResult = await openBookingProduction({
      bookingsIds: unique(newBookingsIds),
      direction: getDirectionFromPassengersData({
        passengersData,
      }),
    });

    if (handleProduceResult.success) {
      onCloseDrawer();
      setBulkBookingStateToForm();
      fetchBookings();
    }
  });

  const handleProcessFile = useEvent(async (): Promise<void> => {
    const file = getValues(BOOKING_UPLOAD_FORM_FIELDS.file);

    if (!file) {
      Log.warnAndToast(t(errors.common.fileIsNotSelected));
      return;
    }

    const { entityCreateFields } = getValues();

    try {
      const result = await bulkUploadBookingsCsvFile({ file, t });
      const bookingPrepareResult = await bookingsPrepare({
        data: result.data,
        entityCreateFields: entityCreateFields?.map(
          ({ entity, customFieldId, isAllow }) => ({
            entity,
            customFieldId,
            isAllow,
          }),
        ),
      });

      if (!bookingPrepareResult.success || !bookingPrepareResult.data) {
        errorAndToastSimpleResult(bookingPrepareResult, t);
        return;
      }

      setPrepareResult(bookingPrepareResult.data);
      setTimeout(() => onSaveClick());
    } catch (error) {
      Log.errorAndToast(t(errors.common.unknownError), error);
    }
  });

  const isSaveDisabled =
    isPreviewStep && (isBulkBookingSetProceeding || successfullyBooked <= 0);

  return (
    <BaseDrawer onCloseDrawer={onCloseClick} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("booking.bulk.setBookings")}
        onCloseDrawer={onCloseClick}
        Footer={
          <BulkCreateBookingFooter
            saveLabel={saveLabel}
            onSaveClick={handleProcessFile}
            cancelLabel={cancelLabel}
            onCancelClick={onCancelClick}
            isSaveDisabled={isSaveDisabled}
            isLoading={isBulkBookingSetProceeding}
            isProduceButtonActive={isProduceButtonActive}
            onProduce={onProduce}
          />
        }
      >
        <PageContextWrapper entity="booking">
          <FormProvider {...methods}>
            {isPreviewStep ? (
              <PreviewStepForm
                bookingCount={bookingCount}
                successfullyBooked={successfullyBooked}
                uploadConflicts={prepareResult?.conflicts}
                onSaveClick={onSaveClick}
                onRefresh={refreshHandler}
                onAbort={abortHandler}
              />
            ) : (
              <BulkUploadBookingsForm />
            )}
          </FormProvider>
        </PageContextWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};

export const BulkUploadBookings: FC<
  IBulkUploadBookingsProps & IInjectedDrawerProps
> = props => (
  <Suspense>
    <BulkUploadBookingsSuspended {...props} />
  </Suspense>
);
